/*
  iPod builder: one dropdown per part, limited to the verified stock in snippets/ipod-stock.liquid.
  Parts already in the cart (as line-item properties of earlier builds) are subtracted from stock.
  The cart and checkout cannot enforce part-level stock on their own, so keep the Shopify inventory
  of the build product in step with the parts list.
*/
const STEPS = [
  { key: 'ssd', title: 'Storage (M.2 SSD + ZIF adapter)' },
  { key: 'battery', title: 'Battery' },
  { key: 'faceplate', title: 'Faceplate color' },
  { key: 'wheel', title: 'Click wheel color' },
  { key: 'dot', title: 'Center button color' },
  { key: 'backplate', title: 'Backplate color' },
];

const propName = (step) => `Build: ${step.title}`;
const label = (opt) => `${opt.group} ${opt.name}`;

class IpodBuilder extends HTMLElement {
  choice = {};
  used = {};

  async connectedCallback() {
    this.stock = JSON.parse(this.querySelector('[data-stock]').textContent);
    this.fieldsEl = this.querySelector('[data-steps]');
    this.addBtn = this.querySelector('[data-add]');
    this.msg = this.querySelector('[data-msg]');
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    await this.loadCartUsage();
    this.pickDefaults();
    this.render();
    this.addBtn.addEventListener('click', () => this.addToCart());
    this.querySelector('[data-shuffle]').addEventListener('click', () => this.shuffle());
  }

  async loadCartUsage() {
    this.used = {};
    try {
      const cart = await (await fetch('/cart.js')).json();
      for (const item of cart.items) {
        for (const step of STEPS) {
          const value = item.properties?.[propName(step)];
          if (!value) continue;
          const opt = this.stock[step.key].find((o) => label(o) === value);
          if (!opt) continue;
          const id = `${step.key}:${opt.id}`;
          this.used[id] = (this.used[id] || 0) + item.quantity;
        }
      }
    } catch {
      /* cart unavailable: fall back to full stock */
    }
  }

  left(key, opt) {
    return opt.qty - (this.used[`${key}:${opt.id}`] || 0);
  }

  available(key) {
    return this.stock[key].filter((o) => this.left(key, o) > 0);
  }

  pickDefaults() {
    for (const step of STEPS) this.choice[step.key] = this.available(step.key)[0] || null;
  }

  shuffle() {
    for (const step of STEPS) {
      const pool = this.available(step.key);
      if (pool.length) this.choice[step.key] = pool[Math.floor(Math.random() * pool.length)];
    }
    this.render();
  }

  render() {
    this.fieldsEl.replaceChildren();
    for (const step of STEPS) {
      const field = document.createElement('div');
      field.className = 'ipod-builder__field';

      const id = `ipod-${step.key}`;
      const lab = document.createElement('label');
      lab.htmlFor = id;
      lab.textContent = step.title;

      const select = document.createElement('select');
      select.id = id;
      select.className = 'ipod-builder__select';

      const groups = new Map();
      for (const opt of this.stock[step.key]) {
        if (!groups.has(opt.group)) {
          const g = document.createElement('optgroup');
          g.label = opt.group;
          groups.set(opt.group, g);
          select.append(g);
        }
        const left = this.left(step.key, opt);
        const o = document.createElement('option');
        o.value = opt.id;
        o.disabled = left <= 0;
        o.textContent = left > 0 ? `${opt.name} (${left} left)` : `${opt.name} (sold out)`;
        o.selected = this.choice[step.key]?.id === opt.id;
        groups.get(opt.group).append(o);
      }
      select.addEventListener('change', () => {
        this.choice[step.key] = this.stock[step.key].find((o) => o.id === select.value);
        this.render();
      });

      field.append(lab, select);
      const sel = this.choice[step.key];
      if (sel?.note) {
        const n = document.createElement('p');
        n.className = 'ipod-builder__note';
        n.textContent = sel.note;
        field.append(n);
      }
      this.fieldsEl.append(field);
    }
    this.updatePreview();
    this.updateSummary();
  }

  updatePreview() {
    const set = (part, prop, key) => {
      const el = this.querySelector(`[data-part="${part}"]`);
      if (el && this.choice[key]?.hex) el.style.setProperty(prop, this.choice[key].hex);
    };
    set('faceplate', '--plate', 'faceplate');
    set('wheel', '--wheelc', 'wheel');
    set('dot', '--dotc', 'dot');
    set('backplate', '--plate', 'backplate');
    const engrave = this.querySelector('[data-engrave]');
    if (engrave) engrave.textContent = this.choice.ssd ? this.choice.ssd.name : '';
  }

  variant() {
    if (this.variants.length === 1) return this.variants[0];
    const ssd = this.choice.ssd?.name;
    const battery = this.choice.battery?.name;
    return this.variants.find((v) => v.o1 === ssd && v.o2 === battery) || null;
  }

  updateSummary() {
    const v = this.variant();
    this.querySelector('[data-price]').textContent = v
      ? new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(v.price / 100)
      : '';
    const complete = STEPS.every((s) => this.choice[s.key]);
    this.addBtn.disabled = !(complete && v && v.available);
    this.msg.textContent = complete ? '' : 'One of the parts is sold out, so this build is not available right now.';
  }

  async addToCart() {
    this.addBtn.disabled = true;
    const properties = {};
    for (const step of STEPS) properties[propName(step)] = label(this.choice[step.key]);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: this.variant().id, quantity: 1, properties }] }),
      });
      if (!res.ok) throw new Error((await res.json()).description || 'Could not add to cart');
      this.msg.textContent = 'Added. Opening your cart...';
      location.href = '/cart';
    } catch (e) {
      this.msg.textContent = e.message;
      this.addBtn.disabled = false;
    }
  }
}

if (!customElements.get('ipod-builder')) customElements.define('ipod-builder', IpodBuilder);
